import { Dispatch, Reducer, Action } from "redux"
import { EmptyRequestState, RequestState } from "../../models/RequestState"
import InstanceClient from "../../services/InstanceClient"
import StatusCode from "../../util/StatusCode"
import { CoreOneRosterMappingTracking, OneRosterTrackingSearchRequest } from "../../models/CoreTrackingOperation"

enum OneRosterTrackingOperationType {
    GET_ONEROSTER_ACTIVITIES = 'GET_ONEROSTER_ACTIVITIES',
    GET_ONEROSTER_GROUPS = 'GET_ONEROSTER_GROUPS',
    UPDATE_ONEROSTER_ACTIVITY_TRACKING = 'UPDATE_ONEROSTER_ACTIVITY_TRACKING',
    UPDATE_ONEROSTER_GROUP_TRACKING = 'UPDATE_ONEROSTER_GROUP_TRACKING',
};

export interface OneRosterTrackingOperationState {
    getOneRosterActivities: RequestState<CoreOneRosterMappingTracking[]>
    getOneRosterGroups: RequestState<CoreOneRosterMappingTracking[]>
    updateOneRosterActivityTracking: EmptyRequestState
    updateOneRosterGroupTracking: EmptyRequestState
};

interface OneRosterActivities extends Action<OneRosterTrackingOperationType> {
    type: OneRosterTrackingOperationType.GET_ONEROSTER_ACTIVITIES
    getOneRosterActivities: RequestState<CoreOneRosterMappingTracking[]>
};

interface OneRosterGroups extends Action<OneRosterTrackingOperationType> {
    type: OneRosterTrackingOperationType.GET_ONEROSTER_GROUPS
    getOneRosterGroups: RequestState<CoreOneRosterMappingTracking[]>
};

interface UpdateOneRosterActivityTracking extends Action<OneRosterTrackingOperationType> {
    type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_ACTIVITY_TRACKING,
    updateOneRosterActivityTracking: EmptyRequestState
};

interface UpdateOneRosterGroupTracking extends Action<OneRosterTrackingOperationType> {
    type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_GROUP_TRACKING,
    updateOneRosterGroupTracking: EmptyRequestState
};

type OneRosterTrackingOperationAction =
    OneRosterActivities
    | OneRosterGroups
    | UpdateOneRosterActivityTracking
    | UpdateOneRosterGroupTracking;

export const GetOneRosterTrackingActivities = (filter: Partial<OneRosterTrackingSearchRequest> = {}) => (dispatch: Dispatch<OneRosterActivities>) => {
    dispatch({
        type: OneRosterTrackingOperationType.GET_ONEROSTER_ACTIVITIES,
        getOneRosterActivities: { code: StatusCode.PENDING }
    });

    InstanceClient.get<CoreOneRosterMappingTracking[]>(`/api/core/trackingoperation/oneroster/activities`, filter)
        .then(res =>
            dispatch({
                type: OneRosterTrackingOperationType.GET_ONEROSTER_ACTIVITIES,
                getOneRosterActivities: { code: StatusCode.COMPLETE, data: res.data }
            }))
        .catch(error =>
            dispatch({
                type: OneRosterTrackingOperationType.GET_ONEROSTER_ACTIVITIES,
                getOneRosterActivities: { code: StatusCode.ERROR, error }
            })
        );
};

export const GetOneRosterTrackingGroups = (filter: Partial<OneRosterTrackingSearchRequest> = {}) => (dispatch: Dispatch<OneRosterGroups>) => {
    dispatch({
        type: OneRosterTrackingOperationType.GET_ONEROSTER_GROUPS,
        getOneRosterGroups: { code: StatusCode.PENDING }
    });

    InstanceClient.get<CoreOneRosterMappingTracking[]>(`/api/core/trackingoperation/oneroster/groups`, filter)
        .then(res =>
            dispatch({
                type: OneRosterTrackingOperationType.GET_ONEROSTER_GROUPS,
                getOneRosterGroups: { code: StatusCode.COMPLETE, data: res.data }
            }))
        .catch(error =>
            dispatch({
                type: OneRosterTrackingOperationType.GET_ONEROSTER_GROUPS,
                getOneRosterGroups: { code: StatusCode.ERROR, error }
            })
        );
};

export const UpdateOneRosterTrackingActivity = (trackingId: string, targetTrackingId: string) => (dispatch: Dispatch<UpdateOneRosterActivityTracking>) => {
    dispatch({
        type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_ACTIVITY_TRACKING,
        updateOneRosterActivityTracking: { code: StatusCode.PENDING }
    });
    const url = `/api/core/trackingoperation/oneroster/activity/${trackingId}/${targetTrackingId}`;
    InstanceClient.post<any, any>(url)
        .then(res =>
            dispatch({
                type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_ACTIVITY_TRACKING,
                updateOneRosterActivityTracking: { code: StatusCode.COMPLETE }
            }))
        .catch(error =>
            dispatch({
                type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_ACTIVITY_TRACKING,
                updateOneRosterActivityTracking: { code: StatusCode.ERROR, error }
            })
        );
};

export const UpdateOneRosterTrackingGroup = (trackingId: string, targetTrackingId: string) => (dispatch: Dispatch<UpdateOneRosterGroupTracking>) => {
    dispatch({
        type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_GROUP_TRACKING,
        updateOneRosterGroupTracking: { code: StatusCode.PENDING }
    });
    const url = `/api/core/trackingoperation/oneroster/group/${trackingId}/${targetTrackingId}`;
    InstanceClient.post<any, any>(url)
        .then(res =>
            dispatch({
                type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_GROUP_TRACKING,
                updateOneRosterGroupTracking: { code: StatusCode.COMPLETE }
            }))
        .catch(error =>
            dispatch({
                type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_GROUP_TRACKING,
                updateOneRosterGroupTracking: { code: StatusCode.ERROR, error }
            })
        );
};

export const ClearUpdateOneRosterTrackingGroup = () => (dispatch: Dispatch<UpdateOneRosterGroupTracking>) => {
    dispatch({
        type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_GROUP_TRACKING,
        updateOneRosterGroupTracking: { code: StatusCode.NONE }
    });
};

export const ClearUpdateOneRosterTrackingActivity = () => (dispatch: Dispatch<UpdateOneRosterActivityTracking>) => {
    dispatch({
        type: OneRosterTrackingOperationType.UPDATE_ONEROSTER_ACTIVITY_TRACKING,
        updateOneRosterActivityTracking: { code: StatusCode.NONE }
    });
};

const initialState: OneRosterTrackingOperationState = {
    getOneRosterActivities: { code: StatusCode.NONE },
    getOneRosterGroups: { code: StatusCode.NONE },
    updateOneRosterActivityTracking: { code: StatusCode.NONE },
    updateOneRosterGroupTracking: { code: StatusCode.NONE }
};

export const reducer: Reducer<OneRosterTrackingOperationState, OneRosterTrackingOperationAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case OneRosterTrackingOperationType.GET_ONEROSTER_ACTIVITIES:
            return {
                ...state,
                getOneRosterActivities: action.getOneRosterActivities
            };
        case OneRosterTrackingOperationType.GET_ONEROSTER_GROUPS:
            return {
                ...state,
                getOneRosterGroups: action.getOneRosterGroups
            };
        case OneRosterTrackingOperationType.UPDATE_ONEROSTER_ACTIVITY_TRACKING:
            return {
                ...state,
                updateOneRosterActivityTracking: action.updateOneRosterActivityTracking
            };
        case OneRosterTrackingOperationType.UPDATE_ONEROSTER_GROUP_TRACKING:
            return {
                ...state,
                updateOneRosterGroupTracking: action.updateOneRosterGroupTracking
            };
        default:
            return state;
    };
};
